import { t } from "i18next";
import { capitalize } from "lodash";
import React from "react";
import { useSelector } from "src/store";
import { ChannelHeader } from "src/ui/shared/table/headers/ChannelHeader";

import { columnFilter } from "@dashboard/devices/components/Table/filters/DeviceTableFilters.utils";
import { selectCreateRolloutDetails } from "@dashboard/devices/store";
import { Paper, TableContainer, TableRow } from "@mui/material";
import { Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import SemverService from "@services/semverService/semverService";
import TimeService from "@services/timeService/timeService";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { useGetOtaFilesQuery } from "../../api";
import { OtaFile } from "../../types";

import { VersionTableActionsCell } from "./VersionTableActionsCell";
import { VersionTableTagsCell } from "./VersionTableTagsCell";

const columns: ColumnDef<OtaFile>[] = [
    {
        header: t("common:label.version"),
        accessorKey: "version",
        sortingFn: (a, b) => SemverService.compare(a.original.version, b.original.version),
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue<string>()}</Typography>
            </MinimalTableCell>
        ),
    },
    {
        header: t("common:label.created"),
        accessorKey: "created",
        cell: (info) => {
            const created = info.getValue<string | null | undefined>();
            return (
                <MinimalTableCell>
                    <Typography>{created ? TimeService.getLocalDate(created, "ISO", "date") : "-"}</Typography>
                </MinimalTableCell>
            );
        },
    },
    {
        header: t("common:label.channel"),
        accessorKey: "channel",
        accessorFn: (row) => capitalize(row.channel ?? "none"),
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 100 }}>
                <Typography>{info.getValue<string>()}</Typography>
            </MinimalTableCell>
        ),
        filterFn: columnFilter,
    },
    {
        id: "tags",
        header: t("common:label.tags"),
        enableSorting: false,
        cell: ({ row }) => <VersionTableTagsCell row={row} />,
    },
    {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => <VersionTableActionsCell row={row} />,
    },
];

type Props = {
    codename: string | null;
    workspaceId: number;
};
export const VersionTable = (props: Props) => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

    const { data: otaData, isFetching } = useGetOtaFilesQuery(
        props.codename ? { codename: props.codename, workspaceId: props.workspaceId } : skipToken,
    );
    const otaFiles = React.useMemo(() => otaData?.list ?? [], [otaData]);

    const details = useSelector(selectCreateRolloutDetails);

    const versionTable = useReactTable<OtaFile>({
        data: otaFiles,
        columns,
        state: { columnFilters },
        onColumnFiltersChange: setColumnFilters,
        initialState: {
            pagination: { pageSize: 12, pageIndex: 0 },
            sorting: [{ id: "version", desc: true }],
        },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const handleResetFilter = () => {
        setColumnFilters([]);
        versionTable.setPageIndex(0);
    };

    return (
        <React.Fragment>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {versionTable.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header}>
                                        {header.column.id === "channel" && (
                                            <ChannelHeader columnFilters={columnFilters} setColumnFilters={setColumnFilters} />
                                        )}
                                    </Header>
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody<OtaFile>
                        table={versionTable}
                        filter={{ isActive: !!columnFilters.length, onResetFilter: handleResetFilter }}
                        noDataMessage={t("pages:rolloutsPage.noVersions")}
                        loading={isFetching}
                    >
                        {({ rows }) =>
                            rows.map((row) => (
                                <HoverRow {...row} selected={details?.selectedId === row.original.fileId} clickable={false}>
                                    {row.getVisibleCells().map((cell) => (
                                        <React.Fragment key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </React.Fragment>
                                    ))}
                                </HoverRow>
                            ))
                        }
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <CustomTablePagination
                component="div"
                rowsPerPageOptions={[12, 24, 48]}
                count={versionTable.getFilteredRowModel().rows.length}
                rowsPerPage={versionTable.getState().pagination.pageSize}
                page={versionTable.getState().pagination.pageIndex}
                onPageChange={(_event, page) => versionTable.setPageIndex(page)}
                onRowsPerPageChange={(e) => versionTable.setPageSize(Number(e.target.value))}
            />
        </React.Fragment>
    );
};
